/**
 * upload/useArtefactUpload.ts — the File/Prompt/Link artefact journey's logic, kept OUT of the
 * components exactly as useUploadOverlay.ts keeps the Dataverse journey out of its skins.
 *
 * WHY A SEPARATE HOOK, not an extension of useUploadOverlay. The Dataverse hook takes a `flow` as a
 * REQUIRED prop and fetches that flow's components on mount — there is no artefact concept in it, and
 * forcing artefact state through it would mean editing journey logic the whole reskin is under
 * standing instructions not to touch. The artefact pipeline has no target environment, no workflow
 * id, no token and no blueprint (the artefact IS the tool), so it is a genuinely parallel flow that
 * branches off P1's "Upload from device" button. This hook is that flow's single source of behaviour;
 * the V3 pages read what it returns, the same split the Dataverse side already uses.
 *
 * TERRITORY (Stream A build-out, agents C1 + C2). C1 owns the artefact-TYPE selector shell and the
 * FILE input; C2 owns the PROMPT/LINK inputs and the guardrail panel. This hook holds the state and
 * handlers BOTH need, so the selector component can stay thin and either input plugs into the same vm.
 *
 * DRAFT PERSISTENCE IS DELIBERATELY SKIPPED for artefacts this slice. draftStore.ts is keyed by
 * <userId>:<workflowId>, and an artefact upload has no workflow id; more fundamentally, a picked File
 * object cannot be serialised to localStorage and re-hydrated, so a "resumed" file draft would be a
 * lie. The details text could be drafted under a synthetic key, but persisting half of a form and
 * silently dropping the other half is worse than persisting none. So the artefact form is in-memory
 * for the life of the overlay — noted here rather than forced into the workflow-scoped draft.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ArtefactType } from '@trailblazer/shared';
import {
  putArtefactBlob,
  submitArtefact,
  type ArtefactBlobRef,
  type ArtefactUploadRequest,
  type ArtefactUploadResult,
  type ToolRefData,
} from '../api';
import { emptyDetails, type DraftDetails } from './draftStore';
import { detailsValid, validateDetails, type DetailsField } from './validation';

/**
 * The KNOWN seed guard lists (0015), used ONLY as a friendly client-side pre-check hint. The SERVER
 * is the authority (route step 3 re-checks the allow/block lists AND re-sniffs the magic bytes from
 * disk); a client cannot lie its way past that. These mirror the seed so the wizard can reject an
 * obviously-wrong file before spending a 250 MB upload — but a 422 from the server is what actually
 * governs, and the UI surfaces the server's code when the two ever disagree (e.g. an owner edited the
 * lists). We do NOT surface these to the client from refdata yet; if that endpoint gains them, swap
 * these constants for the fetched values.
 */
export const CLIENT_ALLOWED_EXT = ['zip', 'docx', 'pptx', 'xlsx', 'pdf'] as const;
export const CLIENT_BLOCKED_EXT = ['exe', 'msi', 'bat', 'ps1', 'py', 'js'] as const;
/** 250 MB — the seeded max_artefact_bytes (0015). Client hint; the server re-asserts it. */
export const CLIENT_MAX_ARTEFACT_BYTES = 262144000;
/** UP-F09: exactly one primary + up to five supporting documents. */
export const SUPPORTING_DOCS_CAP = 5;

/** The lower-cased final extension of a filename, or '' when it has none. Mirrors routes/tools.ts. */
export function fileExt(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

/**
 * A friendly pre-upload reason a file is rejected, or null when it passes the CLIENT hints. The
 * block-list wins over the allow-list (a value in both is blocked), matching the server's guard order.
 */
export function clientFileReason(file: File): string | null {
  const ext = fileExt(file.name);
  if (ext === '') return 'This file has no extension we can check.';
  if ((CLIENT_BLOCKED_EXT as readonly string[]).includes(ext)) {
    return `.${ext} files are not allowed.`;
  }
  if (!(CLIENT_ALLOWED_EXT as readonly string[]).includes(ext)) {
    return `.${ext} files cannot be shared. Allowed: ${CLIENT_ALLOWED_EXT.join(', ')}.`;
  }
  if (file.size > CLIENT_MAX_ARTEFACT_BYTES) {
    return 'This file is larger than the 250 MB limit.';
  }
  if (file.size === 0) return 'This file is empty.';
  return null;
}

/**
 * One file the builder added, tracked with its own transfer state so UP-F11's per-row progress and
 * UP-F11a's per-row non-destructive Retry work independently. A row's `status` walks
 * pending → uploading → uploaded, or lands on `error` (client pre-check failure OR a failed/aborted
 * blob step) WITHOUT touching any sibling row. `blob` is set once the blob step returns; `submit`
 * quotes it back.
 */
export interface ArtefactFileRow {
  /** Stable client id (not the server blobRef) so React keys survive reorder/removal. */
  id: string;
  file: File;
  /** Exactly one row is the primary artefact (UP-F09); the rest are supporting (≤5). */
  role: 'primary' | 'supporting';
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  /** 0..1 upload progress; only meaningful while `uploading`/`uploaded`. */
  progress: number;
  /** The friendly message when `status === 'error'`, shown ON the row (non-destructive). */
  error: string | null;
  /** The blob step's result, present once `uploaded`. */
  blob: ArtefactBlobRef | null;
}

/** A monotonic client id source for file rows. */
let rowSeq = 0;
function nextRowId(): string {
  rowSeq += 1;
  return `af-${rowSeq}-${Date.now().toString(36)}`;
}

export interface ArtefactUploadArgs {
  refData: ToolRefData;
  /** Dismiss the overlay entirely. */
  onClose: () => void;
  /** Called with the server result once the tool is created — the host swaps to the success page. */
  onDone: (result: ArtefactUploadResult) => void;
}

/**
 * The artefact journey as a hook. Returns every value + handler the pages read. C1's type selector and
 * file input read `artefactType`/`files` and call `setArtefactType`/`addFiles`/`removeFile`/`setPrimary`/
 * `retryFile`; C2's prompt/link inputs read `promptText`/`linkUrl` and call their setters; both read
 * `details`/`detailErrors` for the shared Details step and `outstandingItems` for the guardrail panel.
 */
export function useArtefactUpload(args: ArtefactUploadArgs) {
  const { refData, onClose, onDone } = args;

  const defaults = useMemo(
    () => ({
      categoryId:
        refData.categories.find((o) => o.label === 'Automations')?.id ??
        refData.categories[0]?.id ??
        0,
      accessId:
        refData.accessRequirements.find((o) => o.label === 'All employees')?.id ??
        refData.accessRequirements[0]?.id ??
        0,
      departmentId:
        refData.departments.find((o) => o.label === 'Digital & IT')?.id ??
        refData.departments[0]?.id ??
        0,
    }),
    [refData],
  );

  // File is the default selected type (UP-F04).
  const [artefactType, setArtefactTypeState] = useState<ArtefactType>('file');
  const [files, setFiles] = useState<ArtefactFileRow[]>([]);
  const [promptText, setPromptText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  // The shared Details answers. Kept in the same DraftDetails shape the Dataverse form uses, so
  // validateDetails and the design's Details fields are reused verbatim. `requirements` is unused on
  // the artefact path (no connectors), so it stays blank and is never sent.
  const [details, setDetails] = useState<DraftDetails>(() => emptyDetails(defaults));
  const [setupInstructions, setSetupInstructions] = useState('');
  const [attested, setAttested] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  // Per-row abort controllers, so removing/retrying a file cancels only its own in-flight upload.
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  // The artefact path never requires the connector "Requirements" field.
  const detailErrors = useMemo(
    () => validateDetails(details, { requirementsRequired: false }),
    [details],
  );

  const primaryRow = useMemo(() => files.find((f) => f.role === 'primary') ?? null, [files]);
  const supportingRows = useMemo(() => files.filter((f) => f.role === 'supporting'), [files]);

  /** True when the selected type has an adequate artefact captured (UP-F08/GC-6). */
  const artefactReady = useMemo(() => {
    if (artefactType === 'file') return primaryRow?.status === 'uploaded';
    if (artefactType === 'prompt') return promptText.trim().length >= 20;
    return /^https:\/\//i.test(linkUrl.trim());
  }, [artefactType, primaryRow, promptText, linkUrl]);

  /**
   * The still-incomplete required items, in the order onSubmit re-checks them. Feeds the guardrail
   * panel / pre-press hint (C2). Never a gate — the button stays enabled; clicking with items
   * outstanding surfaces the first offender. Empty when everything is satisfied.
   */
  const outstandingItems = useMemo(() => {
    const items: string[] = [];
    if (!detailsValid(detailErrors)) items.push('Complete the tool details');
    if (setupInstructions.trim().length < 10) items.push('Add setup instructions');
    if (!artefactReady) {
      items.push(
        artefactType === 'file'
          ? 'Add a primary file'
          : artefactType === 'prompt'
            ? 'Write a prompt of at least 20 characters'
            : 'Add an https:// link',
      );
    }
    if (!attested) items.push('Confirm the data-safety attestation');
    return items;
  }, [detailErrors, setupInstructions, artefactReady, artefactType, attested]);

  // ── Type selector (C1) ───────────────────────────────────────────────────────────────────────

  /** Switch the captured type. Clearing the errorMsg avoids a stale message from the old type. */
  const setArtefactType = useCallback((next: ArtefactType) => {
    setArtefactTypeState(next);
    setErrorMsg(null);
  }, []);

  // ── File input (C1) ──────────────────────────────────────────────────────────────────────────

  /** Kick off one row's blob upload, wiring progress + terminal state back into `files`. */
  const uploadRow = useCallback((row: ArtefactFileRow) => {
    const controller = new AbortController();
    abortControllers.current.set(row.id, controller);

    setFiles((prev) =>
      prev.map((f) =>
        f.id === row.id ? { ...f, status: 'uploading', progress: 0, error: null } : f,
      ),
    );

    void putArtefactBlob(row.file, {
      signal: controller.signal,
      onProgress: ({ loaded, total }) => {
        const p = total > 0 ? loaded / total : 0;
        setFiles((prev) => prev.map((f) => (f.id === row.id ? { ...f, progress: p } : f)));
      },
    }).then((res) => {
      abortControllers.current.delete(row.id);
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id !== row.id) return f;
          if (res.kind === 'ok') {
            return {
              ...f,
              status: 'uploaded',
              progress: 1,
              error: null,
              blob: {
                blobRef: res.body.blobRef,
                filename: row.file.name,
                mimeType: res.body.mimeType,
                byteSize: res.body.byteSize,
                sha256: res.body.sha256,
              },
            };
          }
          // A failed/aborted upload lands on the row itself — every OTHER row is untouched (UP-F11a).
          return { ...f, status: 'error', error: describeBlobFailure(res), blob: null };
        }),
      );
    });
  }, []);

  /**
   * Add files (from the OS picker or a drop). The FIRST valid file with no existing primary becomes
   * primary; the rest are supporting, capped at 5 (UP-F09). A file that fails the client pre-check is
   * still ADDED as an error row so the builder sees why (non-destructive), it simply never uploads.
   */
  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;
      setErrorMsg(null);

      setFiles((prev) => {
        let hasPrimary = prev.some((f) => f.role === 'primary');
        let supportingCount = prev.filter((f) => f.role === 'supporting').length;
        const toUpload: ArtefactFileRow[] = [];
        const next = [...prev];

        for (const file of list) {
          const reason = clientFileReason(file);
          let role: 'primary' | 'supporting';
          if (!hasPrimary) {
            role = 'primary';
            hasPrimary = true;
          } else {
            if (supportingCount >= SUPPORTING_DOCS_CAP) {
              // Silently stop past the cap — the row count itself tells the builder the limit is hit,
              // and the DB trigger is the backstop. Surface a message so it is not a mystery.
              setErrorMsg(`Up to ${SUPPORTING_DOCS_CAP} supporting documents.`);
              break;
            }
            role = 'supporting';
            supportingCount += 1;
          }
          const row: ArtefactFileRow = {
            id: nextRowId(),
            file,
            role,
            status: reason ? 'error' : 'pending',
            progress: 0,
            error: reason,
            blob: null,
          };
          next.push(row);
          if (!reason) toUpload.push(row);
        }

        // Start uploads AFTER state is queued, on the next tick, so uploadRow's own setFiles lands
        // on top of this insert rather than racing it.
        if (toUpload.length > 0) {
          queueMicrotask(() => toUpload.forEach((r) => uploadRow(r)));
        }
        return next;
      });
    },
    [uploadRow],
  );

  /** Remove one row (its ✕). Aborts an in-flight upload and, if it was primary, promotes the next. */
  const removeFile = useCallback((id: string) => {
    const controller = abortControllers.current.get(id);
    if (controller) {
      controller.abort();
      abortControllers.current.delete(id);
    }
    setFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      const rest = prev.filter((f) => f.id !== id);
      // If the primary was removed, the first remaining row inherits primary so there is always
      // exactly one while any file exists (UP-F09; the DB unique index is the backstop).
      if (
        removed?.role === 'primary' &&
        rest.length > 0 &&
        !rest.some((f) => f.role === 'primary')
      ) {
        const [first, ...others] = rest;
        return [
          { ...first!, role: 'primary' as const },
          ...others.map((f) => ({ ...f, role: 'supporting' as const })),
        ];
      }
      return rest;
    });
  }, []);

  /** Mark one row primary; the previous primary becomes supporting (exactly one primary, UP-F09). */
  const setPrimary = useCallback((id: string) => {
    setFiles((prev) => {
      if (!prev.some((f) => f.id === id)) return prev;
      return prev.map((f) => ({
        ...f,
        role: (f.id === id ? 'primary' : 'supporting') as 'primary' | 'supporting',
      }));
    });
  }, []);

  /** UP-F11a: retry ONE errored row's upload, preserving every other row's input. */
  const retryFile = useCallback(
    (id: string) => {
      const row = files.find((f) => f.id === id);
      if (!row) return;
      // Client pre-check failures cannot be retried — the file itself is disallowed; the builder must
      // remove it. Only a transport failure (network/server/abort) is retryable.
      if (clientFileReason(row.file)) return;
      uploadRow(row);
    },
    [files, uploadRow],
  );

  // ── Submit ───────────────────────────────────────────────────────────────────────────────────

  /**
   * Create the tool. Re-checks every gate the guardrail panel surfaces (the button is never disabled),
   * assembles the discriminated body B1's route expects, and posts. On success hands the result up so
   * the host swaps to the success page; every failure keeps the builder's input.
   */
  const onSubmit = useCallback(async () => {
    setTried(true);
    setErrorMsg(null);

    if (!detailsValid(detailErrors)) {
      setErrorMsg('Some details are incomplete. Fix the highlighted fields.');
      return;
    }
    if (setupInstructions.trim().length < 10) {
      setErrorMsg('Add setup instructions (at least 10 characters).');
      return;
    }
    if (!artefactReady) {
      setErrorMsg(
        artefactType === 'file'
          ? 'Add a primary file before you share this.'
          : artefactType === 'prompt'
            ? 'Write a prompt of at least 20 characters.'
            : 'Add a link that starts with https://.',
      );
      return;
    }
    if (artefactType === 'file') {
      // Belt-and-braces: no supporting row may still be uploading or errored when we submit — its blob
      // ref would be missing. Only fully-uploaded supporting files are sent.
      if (supportingRows.some((f) => f.status === 'uploading')) {
        setErrorMsg('Wait for the supporting files to finish uploading.');
        return;
      }
    }
    if (!attested) {
      // The hard data-safety gate (GC-7). Stay put; the message sits by the tick.
      setErrorMsg('Confirm the data-safety attestation to continue.');
      return;
    }

    const common = {
      name: details.name.trim(),
      description: details.description.trim(),
      problem_solved: details.problemSolved.trim(),
      primary_category_id: details.categoryId,
      access_requirement_id: details.accessId,
      department_id: details.departmentId,
      setup_instructions: setupInstructions.trim(),
      dependencies: details.dependencies.trim(),
      estimated_time_saved: Number(details.estimatedTimeSaved),
      attested,
      ...(details.tags.length > 0 ? { tags: details.tags } : {}),
    };

    let body: ArtefactUploadRequest;
    if (artefactType === 'file') {
      if (!primaryRow?.blob) {
        setErrorMsg('The primary file has not finished uploading.');
        return;
      }
      const supporting = supportingRows
        .filter((f) => f.status === 'uploaded' && f.blob)
        .map((f) => f.blob!);
      body = {
        artefact_type: 'file',
        ...common,
        primary: primaryRow.blob,
        ...(supporting.length > 0 ? { supporting } : {}),
      };
    } else if (artefactType === 'prompt') {
      body = { artefact_type: 'prompt', ...common, prompt_text: promptText.trim() };
    } else {
      body = { artefact_type: 'link', ...common, link_url: linkUrl.trim() };
    }

    setSubmitting(true);
    const res = await submitArtefact(body);
    setSubmitting(false);

    if (res.kind === 'ok' && res.body) {
      onDone(res.body);
      return;
    }
    if (res.kind === 'unauthorized') {
      setErrorMsg('Your session has expired. Sign in again and retry.');
      return;
    }
    // Map the server's authoritative guard codes (route step 3) to a friendly message. These fire only
    // if the client hint and the server disagree (e.g. an owner edited the ext lists) or a crafted ref.
    if (res.kind === 'error') {
      setErrorMsg(describeSubmitFailure(res.status, res.code));
    }
  }, [
    detailErrors,
    setupInstructions,
    artefactReady,
    artefactType,
    supportingRows,
    attested,
    details,
    primaryRow,
    promptText,
    linkUrl,
    onDone,
  ]);

  return {
    // Selected type + its inputs.
    artefactType,
    setArtefactType,
    files,
    primaryRow,
    supportingRows,
    addFiles,
    removeFile,
    setPrimary,
    retryFile,
    promptText,
    setPromptText,
    linkUrl,
    setLinkUrl,
    // Shared details + attestation.
    refData,
    details,
    setDetails,
    detailErrors,
    setupInstructions,
    setSetupInstructions,
    attested,
    setAttested,
    // Derived + submit.
    artefactReady,
    outstandingItems,
    submitting,
    errorMsg,
    tried,
    onSubmit,
    onClose,
  };
}

export type ArtefactUploadVm = ReturnType<typeof useArtefactUpload>;

/** Turn a blob-step failure into a per-row message (UP-F11a). */
function describeBlobFailure(res: {
  kind: 'error' | 'unauthorized';
  status?: number;
  code?: string | null;
}): string {
  if (res.kind === 'unauthorized') return 'Your session expired — sign in and retry.';
  const code = 'code' in res ? res.code : null;
  if (code === 'artefact_too_large') return 'Larger than the 250 MB limit.';
  if (code === 'aborted') return 'Upload cancelled. Retry to try again.';
  if (code === 'network_error') return 'Upload failed — check your connection and retry.';
  if (code === 'empty_body') return 'This file is empty.';
  const status = 'status' in res ? res.status : undefined;
  return `Upload failed${status ? ` (${status})` : ''}. Retry to try again.`;
}

/** Turn a metadata-route failure into a builder-facing message. */
function describeSubmitFailure(status: number, code: string | null): string {
  switch (code) {
    case 'artefact_ext_not_allowed':
      return 'That file type cannot be shared. Allowed: zip, docx, pptx, xlsx, pdf.';
    case 'artefact_ext_blocked':
      return 'That file type is not allowed for security reasons.';
    case 'artefact_content_mismatch':
      return "The file's contents do not match its extension, so it cannot be shared.";
    case 'artefact_too_large':
      return 'The file is larger than the 250 MB limit.';
    case 'invalid_artefact_ref':
      return 'One of the uploaded files could not be found. Remove it and add it again.';
    case 'attestation_required':
      return 'Confirm the data-safety attestation to continue.';
    case 'too_many_tags':
      return 'Up to 5 tags.';
    case 'invalid_request':
      return 'Please check the highlighted fields and try again.';
    default:
      return `Could not share this (${status}${code ? `: ${code}` : ''}). Your input is kept.`;
  }
}

/** The Details fields, re-exported for the pages so they need not reach into validation.ts. */
export type { DetailsField };
